import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { QuotePriceUnit, RestaurantInquiryQuote } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// PATCH .../items/:itemId/quotes/:quoteId — edit ONE quote in place (contract
// verified against local user-mngt-srv 2026-07-07; staging follows on deploy):
//   • price, priceUnit, notes and productId are editable; send only what changed.
//   • priceUnit must name a rung the product's linked master defines (PACK
//     needs a packUnit, ITEM an itemUnit) — otherwise 400. Silently ignored on
//     a pre-update UMS, same as productId, which just leaves the quote
//     unit-less and the UI inferring from defaultUnit.
//   • productId must belong to the SAME supplier as the quote — cross-supplier
//     is a 400 "Replacement product must belong to the same supplier".
//   • On a pre-update UMS, productId is silently IGNORED (200, old product
//     kept) — callers that send it must compare the returned productId and
//     surface the mismatch instead of showing a phantom success.
//   • 400 "Cannot edit a quote on a terminal-state item" once the item is
//     decided; only PENDING quotes on live items are editable.
//   • syncSupplierPrice: true also upserts supplier_prices with the quote's
//     (updated) price; the stored price must be > 0 (400 otherwise). Ignored
//     by a pre-update UMS.
// Returns the updated quote (with the product summary).
type TData = RestaurantInquiryQuote;
type TError = AxiosError;
type TVariables = {
  id: number | string;
  itemId: number;
  quoteId: number;
  price?: number;
  priceUnit?: QuotePriceUnit;
  notes?: string;
  productId?: number;
  syncSupplierPrice?: boolean;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUpdateInquiryItemQuoteApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({
    id,
    itemId,
    quoteId,
    price,
    priceUnit,
    notes,
    productId,
    syncSupplierPrice,
  }: TVariables): Promise<TData> => {
    const body: Record<string, unknown> = {};
    if (price != null) body.price = price;
    if (priceUnit != null) body.priceUnit = priceUnit;
    if (notes != null) body.notes = notes;
    if (productId != null) body.productId = productId;
    if (syncSupplierPrice != null) body.syncSupplierPrice = syncSupplierPrice;
    const response = await http.patch(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes/${quoteId}`, body);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper<TData, TError, TVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'update-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
